import { TrendUp, TrendDown, Warning, ChartBar } from '@phosphor-icons/react'
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

// Accent functional palette
const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

function getClassification(percentage) {
  if (percentage >= 85) return 'Excepcional'
  if (percentage >= 70) return 'Excelente'
  if (percentage >= 55) return 'Muy Bueno'
  if (percentage >= 45) return 'Bueno'
  if (percentage >= 30) return 'Correcto'
  return 'Aprobado'
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

export default function BREAMAnalysis({ project, BREEAM_CRITERIA }) {
  if (!project || !project.evaluations || Object.keys(project.evaluations).length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem', color: '#B0A89E', fontFamily: "'DM Sans', sans-serif" }}>
        <ChartBar size={56} style={{ display: 'block', margin: '0 auto 1rem', opacity: 0.25, color: '#7A6A5A' }} />
        <h3 style={{ color: '#7A6A5A', margin: '0 0 0.5rem', fontWeight: '600' }}>Sin evaluaciones todavía</h3>
        <p style={{ fontSize: '0.875rem', color: '#B0A89E' }}>Evalúa algunos criterios primero para ver el análisis</p>
      </div>
    )
  }

  const categoriesData = Object.entries(BREEAM_CRITERIA).map(([catName, catData]) => {
    const catEvals = Object.values(project.evaluations).filter(e => e.category === catName)
    const points = catEvals.reduce((sum, e) => sum + (e.puntos_auditoria ?? e.points_obtained ?? 0), 0)
    const maxPoints = catData.maxPoints
    const percentage = Math.round((points / maxPoints) * 100)
    return {
      name: catName,
      points,
      maxPoints,
      percentage,
      color: catData.color,
      evaluated: catEvals.length,
      total: Object.keys(catData.criteria).length
    }
  })

  const sorted = [...categoriesData].filter(c => c.evaluated > 0).sort((a, b) => b.percentage - a.percentage)
  const best = sorted.slice(0, 3)
  const worst = sorted.slice(-3).reverse()

  const allEvaluations = Object.values(project.evaluations)
  const lowScoring = allEvaluations
    .filter(e => {
      const critData = BREEAM_CRITERIA[e.category]?.criteria[e.criteria_code]
      if (!critData) return false
      const finalPts = e.puntos_auditoria ?? e.points_obtained ?? 0
      const pct = (finalPts / critData.points) * 100
      return pct < 50
    })
    .slice(0, 5)

  // Puntuación oficial BREEAM ES = ponderada por categorías (Tabla 5 del
  // manual NC 2015), no bruta. El bruto (totalPoints / maxPoints) desvirtúa
  // la clasificación porque no todas las categorías pesan lo mismo. Cuando
  // la app cargó el proyecto ya calculó project.breeamPct con la ponderación
  // correcta (ver computeProjectStats en App.js). Usamos ese valor si está
  // disponible; solo caemos al bruto si el proyecto se cargó sin stats.
  const totalPct = project.breeamPct != null
    ? project.breeamPct
    : Math.round(((project.totalPoints || 0) / (project.maxPoints || 110)) * 100)

  const tooltipStyle = {
    contentStyle: {
      background: '#FFFFFF',
      border: '1px solid #DDD5C8',
      borderRadius: '4px',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '0.8rem',
      color: '#2C2017',
      boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Resumen general */}
      <div style={{
        background: '#3B2F2F',
        borderRadius: '8px',
        padding: '1.75rem 2rem',
        color: '#F5F0EA',
        display: 'flex',
        alignItems: 'center',
        gap: '2rem',
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.35rem', fontWeight: '500' }}>
            Puntuación Total
          </div>
          <div style={{ fontSize: '2.75rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1, marginBottom: '0.25rem', color: '#F5F0EA' }}>
            {project.totalPoints}
            <span style={{ fontSize: '1.25rem', color: '#B0A89E', fontWeight: '400', marginLeft: '0.4rem' }}>
              / {project.maxPoints || 110}
            </span>
          </div>
          <div style={{ fontSize: '1rem', fontWeight: '500', color: '#EDE7DE' }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{totalPct.toFixed(1)}%</span>
            {' — '}{getClassification(totalPct)}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ background: 'rgba(245,240,234,0.12)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
            <div style={{
              background: ACCENT.olive,
              height: '100%',
              width: `${totalPct}%`,
              borderRadius: '4px',
              transition: 'width 0.5s'
            }} />
          </div>
        </div>
      </div>

      {/* Gráfico de barras por categoría */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Puntuación por Categoría
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={categoriesData} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DE" vertical={false} />
            <XAxis
              dataKey="name"
              angle={-40}
              textAnchor="end"
              height={90}
              style={{ fontSize: '0.7rem', fill: '#7A6A5A', fontFamily: "'DM Sans', sans-serif" }}
              tick={{ fill: '#7A6A5A' }}
            />
            <YAxis tick={{ fill: '#7A6A5A', fontSize: '0.75rem' }} axisLine={false} tickLine={false} />
            <Tooltip {...tooltipStyle} />
            <Bar dataKey="maxPoints" fill="#EDE7DE" name="Máximo" radius={[2,2,0,0]} />
            <Bar dataKey="points" fill={ACCENT.olive} name="Obtenido" radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico circular */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.25rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
          Distribución de Puntuación
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={categoriesData.filter(c => c.points > 0)}
              dataKey="points"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name, percentage }) => `${name}: ${percentage}%`}
              labelLine={{ stroke: '#DDD5C8' }}
            >
              {categoriesData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip {...tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Mejores categorías */}
      {best.length > 0 && (
        <div style={CARD}>
          <h3 style={{
            margin: '0 0 1rem',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#2C2017',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <TrendUp size={18} style={{ color: ACCENT.olive }} />
            Categorías con Mejor Desempeño
          </h3>
          {best.map((cat, i) => (
            <div key={i} style={{
              padding: '0.75rem 1rem',
              marginBottom: '0.5rem',
              background: '#FAF7F3',
              borderRadius: '4px',
              borderLeft: `3px solid ${ACCENT.olive}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#2C2017', fontSize: '0.875rem' }}>{cat.name}</div>
                <div style={{ fontSize: '0.775rem', color: '#7A6A5A', marginTop: '0.1rem' }}>
                  {cat.evaluated}/{cat.total} criterios evaluados
                </div>
              </div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                fontFamily: "'IBM Plex Mono', monospace",
                color: ACCENT.olive
              }}>
                {cat.percentage}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Peores categorías */}
      {worst.length > 0 && (
        <div style={CARD}>
          <h3 style={{
            margin: '0 0 1rem',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#2C2017',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <TrendDown size={18} style={{ color: ACCENT.coral }} />
            Áreas de Mejora
          </h3>
          {worst.map((cat, i) => (
            <div key={i} style={{
              padding: '0.75rem 1rem',
              marginBottom: '0.5rem',
              background: '#FDF5F3',
              borderRadius: '4px',
              borderLeft: `3px solid ${ACCENT.coral}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#2C2017', fontSize: '0.875rem' }}>{cat.name}</div>
                <div style={{ fontSize: '0.775rem', color: '#7A6A5A', marginTop: '0.1rem' }}>
                  {cat.evaluated}/{cat.total} criterios evaluados
                </div>
              </div>
              <div style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                fontFamily: "'IBM Plex Mono', monospace",
                color: ACCENT.coral
              }}>
                {cat.percentage}%
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Criterios prioritarios */}
      {lowScoring.length > 0 && (
        <div style={CARD}>
          <h3 style={{
            margin: '0 0 1rem',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#2C2017',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Warning size={18} style={{ color: ACCENT.amber }} />
            Criterios Prioritarios para Mejorar
          </h3>
          {lowScoring.map((ev, i) => {
            const critData = BREEAM_CRITERIA[ev.category]?.criteria[ev.criteria_code]
            return (
              <div key={i} style={{
                padding: '0.75rem 1rem',
                marginBottom: '0.5rem',
                background: '#FDF9F2',
                borderRadius: '4px',
                borderLeft: `3px solid ${ACCENT.amber}`
              }}>
                <div style={{ fontWeight: '600', color: '#2C2017', fontSize: '0.875rem' }}>
                  {ev.criteria_code} — {ev.criteria_name}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#7A6A5A', marginTop: '0.2rem', fontFamily: "'IBM Plex Mono', monospace" }}>
                  {ev.points_obtained}/{critData?.points} pts · {ev.category}
                </div>
                <div style={{ fontSize: '0.775rem', color: ACCENT.amber, marginTop: '0.35rem', fontWeight: '500' }}>
                  Completa los criterios faltantes para aumentar la puntuación
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
